import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../services/api';
import styles from './Admin.module.css';

export default function AdminUsersPage({ user }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const registrationLink = `${window.location.origin}${process.env.PUBLIC_URL || ''}/register`;

  const load = async () => {
    try {
      const [usersData, rolesData] = await Promise.all([
        api.getUsers(user.credential),
        api.getManageableRoles(user.credential),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setError(null);
    } catch (e) {
      setError('אין הרשאה או שגיאה בטעינה');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u => (u.email || '').toLowerCase().includes(q));
  }, [users, search]);

  const toggleRole = async (u, roleName) => {
    const has = u.roles.includes(roleName);
    // Self-lockout guard: an admin may edit their own roles, but must not
    // remove their own 'Admin' role (which would lock them out of this screen).
    if (u.email === user.email && roleName === 'Admin' && has) {
      alert('אי אפשר להסיר לעצמך את הרשאת האדמין');
      return;
    }
    const newRoles = has ? u.roles.filter(r => r !== roleName) : [...u.roles, roleName];
    setSavingId(u.id);
    try {
      await api.setUserRoles(user.credential, u.id, newRoles);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, roles: newRoles } : x));
    } catch (e) {
      alert('שגיאה בעדכון הרשאה');
    } finally {
      setSavingId(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(registrationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      window.prompt('העתק את הקישור:', registrationLink);
    }
  };

  if (loading) return <div className={styles.container}><p>טוען...</p></div>;
  if (error) return <div className={styles.container}><p className={styles.error}>{error}</p></div>;

  return (
    <div className={styles.container}>
      <h2>ניהול משתמשים ({users.length})</h2>

      {/* Registration link */}
      <div className={styles.linkBox}>
        <div className={styles.linkInfo}>
          <div className={styles.linkTitle}>קישור רישום</div>
          <div className={styles.linkUrl}>{registrationLink}</div>
        </div>
        <button className={styles.copyBtn} onClick={copyLink}>
          {copied ? '✓ הועתק' : 'העתק'}
        </button>
      </div>
      <p className={styles.hint}>שלח את הקישור למועמדים. לאחר רישום, שייך להם הרשאות כאן.</p>

      <input
        className={styles.searchInput}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="חיפוש לפי אימייל..."
      />

      <div className={styles.userList}>
        {filtered.map((u) => (
          <div key={u.id} className={styles.userCard}>
            <div className={styles.userInfo}>
              <div className={styles.userName}>{u.email}</div>
              {u.roles.length === 0 && <div className={styles.userDate}>ללא הרשאות</div>}
            </div>
            <div className={styles.roleChecks}>
              {roles.map((role) => {
                const isSelf = u.email === user.email;
                const checked = u.roles.includes(role.name);
                // You can edit your own roles, but you cannot remove your own
                // 'Admin' role (self-lockout guard) — that one checkbox stays locked.
                const lockOwnAdmin = isSelf && role.name === 'Admin' && checked;
                return (
                  <label key={role.name} className={styles.roleCheck}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={savingId === u.id || lockOwnAdmin}
                      onChange={() => toggleRole(u, role.name)}
                    />
                    {role.displayName}
                  </label>
                );
              })}
              {u.email === user.email && <span className={styles.youBadge}>(אתה)</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
